/**
 * Task Dependency Utility
 *
 * Defines which tasks must be completed before others can be started.
 * Used to disable/lock tiles on the Foundation dashboard until prerequisites are met.
 */

export const TASK_DEPENDENCIES = {
  // Foundation tasks (no dependencies)
  'project-info': [],
  'knowledge-hub': [],

  // Analysis
  'features': ['project-info'],
  'user-roles': ['project-info', 'features'],
  'user-stories': ['project-info', 'features', 'user-roles'],
  'process-flows': ['project-info', 'user-stories', 'user-roles'],

  // UI/UX
  'design-references': [],
  'design-guidelines': ['project-info', 'user-stories', 'process-flows', 'design-references'],
  'wireframes': ['user-stories', 'process-flows', 'design-references'],
  'design-system': ['wireframes', 'design-guidelines', 'design-references'],
  'designs': ['wireframes', 'design-system'],

  // Technical
  'field-validations': ['user-stories', 'process-flows'],
  'tech-stack': ['user-stories', 'field-validations', 'project-info'],
  'architecture': ['tech-stack', 'project-info', 'user-stories', 'process-flows'],
  'dev-guidelines': ['tech-stack', 'architecture'],
  'api-guidelines': ['tech-stack', 'architecture', 'dev-guidelines'],
  'security-guidelines': ['tech-stack', 'dev-guidelines', 'architecture'],
  'user-permissions': ['user-roles', 'security-guidelines'],
  'unit-testing': ['dev-guidelines', 'tech-stack', 'user-stories', 'process-flows', 'architecture'],

  // Quality
  'functional-testing': ['unit-testing', 'tech-stack', 'user-stories', 'process-flows', 'architecture'],
  'automated-testing': ['functional-testing', 'tech-stack', 'user-stories', 'process-flows', 'architecture'],
  'automation-testing': ['functional-testing', 'tech-stack', 'user-stories', 'process-flows', 'architecture'],
  'security-testing': ['security-guidelines', 'tech-stack', 'user-stories', 'process-flows', 'architecture'],
  'performance-testing': ['tech-stack', 'user-stories', 'process-flows', 'architecture'],

  // Infrastructure
  'git-guidelines': ['dev-guidelines', 'user-stories', 'process-flows', 'architecture'],
  'cicd-pipeline': ['git-guidelines', 'tech-stack', 'architecture', 'user-stories', 'process-flows'],
  'cloud-deployment': ['cicd-pipeline', 'tech-stack', 'architecture', 'user-stories', 'process-flows'],
  'deployment': ['cicd-pipeline', 'tech-stack', 'architecture', 'user-stories', 'process-flows'],
  'aws-deployment': ['cicd-pipeline', 'tech-stack', 'architecture', 'user-stories', 'process-flows'],

  // Integration
  'integration': ['project-info', 'process-flows', 'user-stories'],
};

/**
 * Aliases for tile IDs that may differ between frontend and backend.
 * Maps frontend tile ID → canonical dependency map key.
 */
const TASK_ID_ALIASES = {
  'uiux-references': 'design-references',
  'uiux-guidelines': 'design-guidelines',
  'uiux-style-guide': 'design-system',
  'uiux-layout': 'designs',
  'ci-cd-pipeline': 'cicd-pipeline',
  'code-version-control': 'git-guidelines',
};

/**
 * Reverse aliases: canonical dependency key → possible frontend tile IDs
 */
const REVERSE_ALIASES = {};
Object.entries(TASK_ID_ALIASES).forEach(([alias, canonical]) => {
  if (!REVERSE_ALIASES[canonical]) REVERSE_ALIASES[canonical] = [];
  REVERSE_ALIASES[canonical].push(alias);
});

/**
 * Resolve a tile ID to its canonical dependency map key
 */
const resolveTaskId = (taskId) => {
  if (TASK_DEPENDENCIES[taskId] !== undefined) return taskId;
  return TASK_ID_ALIASES[taskId] || taskId;
};

/**
 * Check if a dependency is met (completed) considering ID aliases
 */
const isDependencyMet = (depId, completedTasks) => {
  if (completedTasks.includes(depId)) return true;
  // Check if any alias of this dependency is completed
  const aliases = REVERSE_ALIASES[depId] || [];
  return aliases.some(alias => completedTasks.includes(alias));
};

/**
 * Check if a task is locked (has unmet dependencies)
 * @param {string} taskId - The tile/task ID
 * @param {string[]} completedTasks - Array of completed task IDs from project
 * @returns {boolean} True if the task is locked
 */
export const isTaskLocked = (taskId, completedTasks = []) => {
  const canonicalId = resolveTaskId(taskId);
  const deps = TASK_DEPENDENCIES[canonicalId];
  if (!deps || deps.length === 0) return false;
  return deps.some(dep => !isDependencyMet(dep, completedTasks));
};

/**
 * Get the list of unmet dependency task IDs
 * @param {string} taskId - The tile/task ID
 * @param {string[]} completedTasks - Array of completed task IDs from project
 * @returns {string[]} Array of unmet dependency IDs
 */
export const getUnmetDependencies = (taskId, completedTasks = []) => {
  const canonicalId = resolveTaskId(taskId);
  const deps = TASK_DEPENDENCIES[canonicalId];
  if (!deps || deps.length === 0) return [];
  return deps.filter(dep => !isDependencyMet(dep, completedTasks));
};

/**
 * Convert a task ID to a human-readable display name
 */
const TASK_DISPLAY_NAMES = {
  'project-info': 'Project Info',
  'knowledge-hub': 'Knowledge Hub',
  'features': 'Features',
  'user-roles': 'User Roles',
  'user-stories': 'User Stories',
  'process-flows': 'Process Flows',
  'design-references': 'UI/UX References',
  'design-guidelines': 'UI/UX Guidelines',
  'wireframes': 'Wireframes',
  'design-system': 'UI/UX Style Guide',
  'designs': 'UI/UX Layout',
  'field-validations': 'Field Validations',
  'tech-stack': 'Technology Stack',
  'architecture': 'Architecture',
  'dev-guidelines': 'Development Guidelines',
  'api-guidelines': 'API Guidelines',
  'security-guidelines': 'Security Guidelines',
  'user-permissions': 'User Permissions',
  'unit-testing': 'Unit Testing',
  'functional-testing': 'Functional Testing',
  'automated-testing': 'Automated Testing',
  'automation-testing': 'Automated Testing',
  'security-testing': 'Security Testing',
  'performance-testing': 'Performance Testing',
  'git-guidelines': 'Git Guidelines',
  'cicd-pipeline': 'CI/CD Pipeline',
  'cloud-deployment': 'Cloud Deployment',
  'deployment': 'Deployment',
  'aws-deployment': 'AWS Deployment',
  'integration': 'Integration',
};

export const getTaskDisplayName = (taskId) => {
  return TASK_DISPLAY_NAMES[taskId] || taskId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Build a tooltip string explaining why a task is locked
 * @param {string} taskId - The tile/task ID
 * @param {string[]} completedTasks - Array of completed task IDs
 * @returns {string} Tooltip text, or empty string if not locked
 */
export const getLockedTooltip = (taskId, completedTasks = []) => {
  const unmet = getUnmetDependencies(taskId, completedTasks);
  if (unmet.length === 0) return '';
  const names = unmet.map(id => getTaskDisplayName(id));
  if (names.length <= 3) {
    return `Complete ${names.join(', ')} to unlock`;
  }
  return `Complete ${names.slice(0, 3).join(', ')} and ${names.length - 3} more to unlock`;
};
